import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';
import { GetAppliedNode, GetDefinitionNode } from './testUtil';

type Result = ReturnType<typeof useTestByEnvironment>[0];

export type TestResponse = NonNullable<Result['data']>;

export type RequestedTestAppliedNode = GetAppliedNode<'tests', TestResponse>;

export type RequestedTestDefinitionNode = GetDefinitionNode<'tests', TestResponse>;

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetTestByUniqueId = graphql(/* GraphQL */ `
  query Test($environmentId: BigInt!, $uniqueIds: [String!], $userId: BigInt) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        tests(first: 1, filter: { uniqueIds: $uniqueIds }) {
          edges {
            node {
              fqn
              uniqueId
              config
              tags
              description
              meta
              columnName
              compiledCode
              filePath
              patchPath
              name
              rawCode
              projectId
              lastKnownResult
              testType
              expect
              given
              numExpectRows
              numGiven
              numGivenRows
              model
              parents {
                uniqueId
                resourceType
                name
                __typename
              }
              executionInfo {
                lastRunId
                lastRunStatus
                executeCompletedAt
                lastJobDefinitionId
                executionTime
                lastRunError
              }
              testMetadata {
                name
                kwargs
              }
            }
          }
        }
      }
    }
  }
`);

type TestByEnvironmentParams = {
  environmentId: number | undefined;
  userId: number | null;
  uniqueId: string;
  pause?: boolean;
};

export const useTestByEnvironment = ({
  environmentId = 0,
  userId,
  uniqueId,
  pause,
}: TestByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetTestByUniqueId,
    variables: { environmentId, uniqueIds: [uniqueId], userId },
    pause: pause || !environmentId || !uniqueId,
  });

  useCacheMonitor({
    applied: query[0].data?.environment.applied?.lastUpdatedAt,
  });
  return query;
};
